import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useBusiness } from '../context/BusinessContext';
import { updateBusiness, listMembers, addMember, removeMember, getCalendarOAuthUrl, getCalendarStatus, disconnectCalendar } from '../services/api';
import { Business, BusinessMember, Service, FAQ } from '../types';
import { PlusIcon, TrashIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

export default function SettingsPage() {
  const { currentBusiness, currentMembership, refreshBusinesses } = useBusiness();
  const [activeTab, setActiveTab] = useState('general');

  if (!currentBusiness) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Please select a business</p>
      </div>
    );
  }

  // Client portal only shows these tabs (no Voice or Advanced - those are agency-controlled)
  const tabs = [
    { id: 'general', name: 'General' },
    { id: 'services', name: 'Services' },
    { id: 'faqs', name: 'FAQs' },
    { id: 'team', name: 'Team' },
  ];

  return (
    <div>
      <h1 className="page-header">Settings</h1>

      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      <div className="card">
        {activeTab === 'general' && (
          <GeneralSettings business={currentBusiness} onUpdate={refreshBusinesses} />
        )}
        {activeTab === 'services' && (
          <ServicesSettings business={currentBusiness} onUpdate={refreshBusinesses} />
        )}
        {activeTab === 'faqs' && (
          <FAQsSettings business={currentBusiness} onUpdate={refreshBusinesses} />
        )}
        {activeTab === 'team' && (
          <TeamSettings
            business={currentBusiness}
            currentMembership={currentMembership}
          />
        )}
      </div>
    </div>
  );
}

function GeneralSettings({
  business,
  onUpdate,
}: {
  business: Business;
  onUpdate: () => void;
}) {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    name: business.name,
    phoneNumber: business.phoneNumber,
    timezone: business.timezone,
    greeting: business.greeting,
    transferNumber: business.transferNumber || '',
  });
  const [loading, setLoading] = useState(false);
  const [calendarStatus, setCalendarStatus] = useState<{
    connected: boolean;
    hasRefreshToken: boolean;
  } | null>(null);
  const [calendarLoading, setCalendarLoading] = useState(true);

  useEffect(() => {
    const fetchCalendarStatus = async () => {
      try {
        const status = await getCalendarStatus(business.id);
        setCalendarStatus(status);
      } catch (error) {
        console.error('Error fetching calendar status:', error);
        setCalendarStatus({ connected: false, hasRefreshToken: false });
      } finally {
        setCalendarLoading(false);
      }
    };
    fetchCalendarStatus();

    // Check for OAuth callback result
    const calendarParam = searchParams.get('calendar');
    if (calendarParam === 'connected') {
      alert('Google Calendar connected successfully!');
      fetchCalendarStatus();
    } else if (calendarParam === 'error') {
      alert('Failed to connect Google Calendar. Please try again.');
    }
  }, [business.id, searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await updateBusiness(business.id, formData);
      onUpdate();
      alert('Settings saved!');
    } catch (error) {
      console.error('Error updating business:', error);
      alert('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleConnectCalendar = async () => {
    try {
      const url = await getCalendarOAuthUrl(business.id);
      window.location.href = url;
    } catch (error) {
      console.error('Error getting OAuth URL:', error);
      alert('Failed to initiate calendar connection');
    }
  };

  const handleDisconnectCalendar = async () => {
    if (!confirm('Are you sure you want to disconnect Google Calendar?')) return;

    try {
      await disconnectCalendar(business.id);
      setCalendarStatus({ connected: false, hasRefreshToken: false });
      alert('Google Calendar disconnected');
    } catch (error) {
      console.error('Error disconnecting calendar:', error);
      alert('Failed to disconnect calendar');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="label">Business Name</label>
        <input
          type="text"
          required
          className="input"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        />
      </div>
      <div>
        <label className="label">Phone Number</label>
        <input
          type="tel"
          required
          className="input"
          value={formData.phoneNumber}
          onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
        />
        <p className="text-xs text-gray-500 mt-1">
          This should be your Twilio phone number
        </p>
      </div>
      <div>
        <label className="label">Timezone</label>
        <select
          className="input"
          value={formData.timezone}
          onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
        >
          <option value="America/New_York">Eastern Time</option>
          <option value="America/Chicago">Central Time</option>
          <option value="America/Denver">Mountain Time</option>
          <option value="America/Los_Angeles">Pacific Time</option>
        </select>
      </div>
      <div>
        <label className="label">Greeting Message</label>
        <textarea
          className="input"
          rows={3}
          value={formData.greeting}
          onChange={(e) => setFormData({ ...formData, greeting: e.target.value })}
        />
        <p className="text-xs text-gray-500 mt-1">
          This message will be spoken when answering calls
        </p>
      </div>
      <div>
        <label className="label">Transfer Number (optional)</label>
        <input
          type="tel"
          className="input"
          value={formData.transferNumber}
          onChange={(e) => setFormData({ ...formData, transferNumber: e.target.value })}
        />
        <p className="text-xs text-gray-500 mt-1">
          Calls will be transferred here when requested
        </p>
      </div>

      {/* Google Calendar Connection */}
      <div className="border-t border-gray-200 pt-6">
        <label className="label">Google Calendar</label>
        {calendarLoading ? (
          <p className="text-sm text-gray-500">Checking calendar status...</p>
        ) : calendarStatus?.connected ? (
          <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-2">
              <CheckCircleIcon className="h-5 w-5 text-green-600" />
              <span className="text-green-800">Google Calendar connected</span>
            </div>
            <button
              type="button"
              onClick={handleDisconnectCalendar}
              className="text-sm text-red-600 hover:text-red-700"
            >
              Disconnect
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <div className="flex items-center gap-2">
              <XCircleIcon className="h-5 w-5 text-gray-400" />
              <span className="text-gray-600">Calendar not connected</span>
            </div>
            <button
              type="button"
              onClick={handleConnectCalendar}
              className="btn-secondary"
            >
              Connect Google Calendar
            </button>
          </div>
        )}
        <p className="text-xs text-gray-500 mt-2">
          Connect your Google Calendar to enable automatic appointment scheduling
        </p>
      </div>

      <button type="submit" disabled={loading} className="btn-primary">
        {loading ? 'Saving...' : 'Save Changes'}
      </button>
    </form>
  );
}

function ServicesSettings({
  business,
  onUpdate,
}: {
  business: Business;
  onUpdate: () => void;
}) {
  const [services, setServices] = useState<Service[]>(business.services || []);
  const [loading, setLoading] = useState(false);

  const addService = () => {
    setServices([
      ...services,
      {
        id: Date.now().toString(),
        name: '',
        description: '',
        duration: 60,
        price: undefined,
      },
    ]);
  };

  const removeService = (id: string) => {
    setServices(services.filter((s) => s.id !== id));
  };

  const updateService = (id: string, updates: Partial<Service>) => {
    setServices(
      services.map((s) => (s.id === id ? { ...s, ...updates } : s))
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await updateBusiness(business.id, { services });
      onUpdate();
      alert('Services saved!');
    } catch (error) {
      console.error('Error updating services:', error);
      alert('Failed to save services');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <p className="text-sm text-gray-600">
        Services are used by the AI to explain what your business offers and to book appointments.
      </p>
      <div className="space-y-4">
        {services.map((service, index) => (
          <div key={service.id} className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium">Service {index + 1}</h3>
              <button
                type="button"
                onClick={() => removeService(service.id)}
                className="text-red-600 hover:text-red-700"
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Name</label>
                <input
                  type="text"
                  required
                  className="input"
                  value={service.name}
                  onChange={(e) =>
                    updateService(service.id, { name: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="label">
                  Duration (minutes)
                  <span className="text-gray-400 font-normal ml-1">- optional</span>
                </label>
                <input
                  type="number"
                  min="15"
                  className="input"
                  value={service.duration || ''}
                  onChange={(e) =>
                    updateService(service.id, { duration: e.target.value ? parseInt(e.target.value) : undefined })
                  }
                />
              </div>
              <div className="col-span-2">
                <label className="label">Description</label>
                <textarea
                  className="input"
                  rows={2}
                  value={service.description}
                  onChange={(e) =>
                    updateService(service.id, { description: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="label">
                  Price
                  <span className="text-gray-400 font-normal ml-1">- optional</span>
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="input"
                  value={service.price || ''}
                  onChange={(e) =>
                    updateService(service.id, {
                      price: e.target.value ? parseFloat(e.target.value) : undefined,
                    })
                  }
                />
              </div>
            </div>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={addService}
        className="btn-secondary flex items-center gap-2"
      >
        <PlusIcon className="h-5 w-5" />
        Add Service
      </button>
      <button type="submit" disabled={loading} className="btn-primary">
        {loading ? 'Saving...' : 'Save Services'}
      </button>
    </form>
  );
}

function FAQsSettings({
  business,
  onUpdate,
}: {
  business: Business;
  onUpdate: () => void;
}) {
  const [faqs, setFaqs] = useState<FAQ[]>(business.faqs || []);
  const [loading, setLoading] = useState(false);

  const addFaq = () => {
    setFaqs([
      ...faqs,
      {
        id: Date.now().toString(),
        question: '',
        answer: '',
        keywords: [],
      },
    ]);
  };

  const removeFaq = (id: string) => {
    setFaqs(faqs.filter((f) => f.id !== id));
  };

  const updateFaq = (id: string, updates: Partial<FAQ>) => {
    setFaqs(faqs.map((f) => (f.id === id ? { ...f, ...updates } : f)));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await updateBusiness(business.id, { faqs });
      onUpdate();
      alert('FAQs saved!');
    } catch (error) {
      console.error('Error updating FAQs:', error);
      alert('Failed to save FAQs');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <p className="text-sm text-gray-600">
        FAQs help the AI answer common questions accurately. The AI will reference these when callers ask related questions.
      </p>
      <div className="space-y-4">
        {faqs.map((faq, index) => (
          <div key={faq.id} className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium">FAQ {index + 1}</h3>
              <button
                type="button"
                onClick={() => removeFaq(faq.id)}
                className="text-red-600 hover:text-red-700"
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="label">Question</label>
                <input
                  type="text"
                  required
                  className="input"
                  value={faq.question}
                  onChange={(e) => updateFaq(faq.id, { question: e.target.value })}
                  placeholder="What are your hours?"
                />
              </div>
              <div>
                <label className="label">Answer</label>
                <textarea
                  required
                  className="input"
                  rows={3}
                  value={faq.answer}
                  onChange={(e) => updateFaq(faq.id, { answer: e.target.value })}
                  placeholder="We're open Monday through Friday from 9 AM to 5 PM..."
                />
              </div>
              <div>
                <label className="label">
                  Keywords
                  <span className="text-gray-400 font-normal ml-1">- comma-separated</span>
                </label>
                <input
                  type="text"
                  className="input"
                  value={faq.keywords.join(', ')}
                  onChange={(e) =>
                    updateFaq(faq.id, {
                      keywords: e.target.value.split(',').map((k) => k.trim()).filter(Boolean),
                    })
                  }
                  placeholder="hours, schedule, open, close"
                />
              </div>
            </div>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={addFaq}
        className="btn-secondary flex items-center gap-2"
      >
        <PlusIcon className="h-5 w-5" />
        Add FAQ
      </button>
      <button type="submit" disabled={loading} className="btn-primary">
        {loading ? 'Saving...' : 'Save FAQs'}
      </button>
    </form>
  );
}

function TeamSettings({
  business,
  currentMembership,
}: {
  business: Business;
  currentMembership: BusinessMember | null;
}) {
  const [members, setMembers] = useState<BusinessMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const data = await listMembers(business.id);
        setMembers(data);
      } catch (error) {
        console.error('Error fetching members:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMembers();
  }, [business.id]);

  const handleRemove = async (memberId: string) => {
    if (!confirm('Are you sure you want to remove this member?')) return;

    try {
      await removeMember(business.id, memberId);
      setMembers(members.filter((m) => m.userId !== memberId));
    } catch (error) {
      console.error('Error removing member:', error);
      alert('Failed to remove member');
    }
  };

  const isAdmin = currentMembership?.role === 'owner' || currentMembership?.role === 'admin';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-medium">Team Members</h3>
        {isAdmin && (
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-secondary flex items-center gap-2"
          >
            <PlusIcon className="h-5 w-5" />
            Add Member
          </button>
        )}
      </div>

      {loading ? (
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-12 bg-gray-200 rounded"></div>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {members.map((member) => (
            <div
              key={member.userId}
              className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
            >
              <div>
                <p className="font-medium">{member.name}</p>
                <p className="text-sm text-gray-500">{member.email}</p>
              </div>
              <div className="flex items-center gap-4">
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    member.role === 'owner'
                      ? 'bg-purple-100 text-purple-800'
                      : member.role === 'admin'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {member.role}
                </span>
                {isAdmin && member.role !== 'owner' && (
                  <button
                    onClick={() => handleRemove(member.userId)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showAddModal && (
        <AddMemberModal
          businessId={business.id}
          onClose={() => setShowAddModal(false)}
          onAdded={(member) => {
            setMembers([...members, member]);
            setShowAddModal(false);
          }}
        />
      )}
    </div>
  );
}

function AddMemberModal({
  businessId,
  onClose,
  onAdded,
}: {
  businessId: string;
  onClose: () => void;
  onAdded: (member: BusinessMember) => void;
}) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const member = await addMember(businessId, email, role);
      onAdded(member);
    } catch (error: any) {
      console.error('Error adding member:', error);
      alert(error.message || 'Failed to add member');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose}></div>
        <div className="relative bg-white rounded-lg max-w-md w-full p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Add Team Member</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="label">Email</label>
              <input
                type="email"
                required
                className="input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">
                User must have an existing account
              </p>
            </div>
            <div>
              <label className="label">Role</label>
              <select
                className="input"
                value={role}
                onChange={(e) => setRole(e.target.value)}
              >
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button type="button" onClick={onClose} className="btn-secondary">
                Cancel
              </button>
              <button type="submit" disabled={loading} className="btn-primary">
                {loading ? 'Adding...' : 'Add Member'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
